import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Megaphone, Save, CheckCircle2, AlertCircle } from "lucide-react";
import { API } from "@/lib/config";

const FORM_LABELS = {
  contact: "General Contact",
  volunteer: "Volunteer Application",
  mentor: "Mentor Application",
  partner: "Partnership Inquiry",
  event: "Event Registration",
  newsletter: "Newsletter Signup",
};

const StatusBadge = ({ ok, label }) => (
  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ${ok ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>
    {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertCircle className="h-3.5 w-3.5" />}
    {label} {ok ? "Connected" : "Not Configured"}
  </span>
);

const EmailSettings = () => {
  const [data, setData] = useState(null);
  const [recipient, setRecipient] = useState("");
  const [forms, setForms] = useState({});
  const [newsletterEnabled, setNewsletterEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [es, cs] = await Promise.all([
        axios.get(`${API}/admin/email-settings`),
        axios.get(`${API}/cms-settings`),
      ]);
      setData(es.data);
      setRecipient(es.data.admin_recipient || "");
      // merge defaults with overrides for editing
      const merged = {};
      Object.keys(es.data.defaults || {}).forEach((k) => {
        merged[k] = { ...es.data.defaults[k], ...(es.data.forms?.[k] || {}) };
      });
      setForms(merged);
      setNewsletterEnabled(cs.data?.newsletter_enabled !== false);
    } catch (err) {
      toast.error("Failed to load email settings.");
    }
  };

  useEffect(() => { load(); }, []);

  const updateForm = (type, field, value) =>
    setForms((f) => ({ ...f, [type]: { ...f[type], [field]: value } }));

  const saveAll = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/admin/email-settings`, { admin_recipient: recipient, forms }),
        axios.put(`${API}/cms-settings`, { newsletter_enabled: newsletterEnabled }),
      ]);
      toast.success("Email & marketing settings saved.");
      load();
    } catch (err) {
      toast.error("Save failed. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <div className="bg-white border border-[#E5E5E5]/50 rounded-2xl p-8 text-sm text-[#4A4A4A]" data-testid="panel-email">Loading…</div>;

  const inputCls = "w-full bg-white border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#E94F9A]/30";

  return (
    <div className="bg-white border border-[#E5E5E5]/50 rounded-2xl p-6 md:p-8 space-y-8" data-testid="panel-email">
      <div className="space-y-3">
        <h3 className="font-serif text-xl font-bold text-[#4B1E5A]">Email & Marketing</h3>
        <p className="font-sans text-sm text-[#4A4A4A]">Resend powers transactional emails (confirmations + admin alerts). MailerLite manages newsletter marketing. Edit recipients and confirmation messages below — no code changes needed.</p>
        <div className="flex flex-wrap gap-3">
          <StatusBadge ok={data.email_enabled} label="Resend" />
          <StatusBadge ok={data.mailerlite_enabled} label="MailerLite" />
          <span className="inline-flex items-center gap-1.5 text-xs text-[#4A4A4A] px-3 py-1.5 rounded-full bg-[#FFF5F9]">
            <Mail className="h-3.5 w-3.5 text-[#E94F9A]" /> Sender: {data.sender_email}
          </span>
        </div>
        {!data.email_enabled && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            Add <strong>RESEND_API_KEY</strong> to backend environment to start sending emails. Confirmations and notifications are queued/skipped until then.
          </p>
        )}
      </div>

      {/* Admin recipient + newsletter toggle */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="font-sans text-xs font-semibold text-[#4B1E5A] uppercase tracking-wider">Admin Notification Recipient</label>
          <input value={recipient} onChange={(e) => setRecipient(e.target.value)} data-testid="email-admin-recipient" className={inputCls} placeholder="info@shesagirlsgirl.org" />
          <p className="text-[11px] text-[#9b9b9b]">Every new submission notification is sent here.</p>
        </div>
        <div className="space-y-2">
          <label className="font-sans text-xs font-semibold text-[#4B1E5A] uppercase tracking-wider flex items-center gap-1.5"><Megaphone className="h-3.5 w-3.5 text-[#E94F9A]" /> Newsletter Signup Section</label>
          <button
            type="button"
            onClick={() => setNewsletterEnabled((v) => !v)}
            data-testid="email-newsletter-toggle"
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${newsletterEnabled ? "bg-[#E94F9A] text-white" : "bg-gray-200 text-gray-600"}`}
          >
            <span className={`h-2.5 w-2.5 rounded-full ${newsletterEnabled ? "bg-white" : "bg-gray-400"}`} />
            {newsletterEnabled ? "Enabled (visible on site)" : "Disabled (hidden)"}
          </button>
          <p className="text-[11px] text-[#9b9b9b]">Show or hide the newsletter signup in the website footer.</p>
        </div>
      </div>

      {/* Per-form confirmation editors */}
      <div className="space-y-5">
        <h4 className="font-serif text-base font-bold text-[#4B1E5A]">Confirmation Emails</h4>
        {Object.keys(forms).map((type) => (
          <details key={type} className="border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid={`email-form-${type}`}>
            <summary className="cursor-pointer px-4 py-3 bg-[#FFF5F9]/40 font-serif text-sm font-bold text-[#4B1E5A]">{FORM_LABELS[type] || type}</summary>
            <div className="p-4 space-y-3">
              <div>
                <label className="text-[11px] font-semibold text-[#4A4A4A] uppercase">Subject</label>
                <input value={forms[type].subject || ""} onChange={(e) => updateForm(type, "subject", e.target.value)} data-testid={`email-${type}-subject`} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#4A4A4A] uppercase">Heading</label>
                <input value={forms[type].heading || ""} onChange={(e) => updateForm(type, "heading", e.target.value)} data-testid={`email-${type}-heading`} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#4A4A4A] uppercase">Intro Message</label>
                <textarea rows={2} value={forms[type].intro || ""} onChange={(e) => updateForm(type, "intro", e.target.value)} data-testid={`email-${type}-intro`} className={inputCls} />
              </div>
              <div>
                <label className="text-[11px] font-semibold text-[#4A4A4A] uppercase">What Happens Next</label>
                <textarea rows={2} value={forms[type].next_steps || ""} onChange={(e) => updateForm(type, "next_steps", e.target.value)} data-testid={`email-${type}-next`} className={inputCls} />
              </div>
            </div>
          </details>
        ))}
      </div>

      <button onClick={saveAll} disabled={saving} data-testid="email-settings-save" className="inline-flex items-center gap-2 bg-[#4B1E5A] text-white rounded-full px-7 py-3 text-sm font-semibold hover:bg-[#E94F9A] transition-colors disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Settings"}
      </button>
    </div>
  );
};

export default EmailSettings;
